/**
 * Shared API Client
 *
 * Axios instance with interceptors for authentication,
 * error handling, and request/response transformation.
 */

import axios from 'axios';

import { AuthenticationException, AuthorizationException, NetworkException, ServerException, ValidationException, NotFoundException, AppException, ErrorCode } from '@/core/exceptions';
import { config } from '@/shared/config';

import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

interface ApiErrorPayload {
  error?: string;
  reason?: string;
  error_description?: string;
  message?: string;
  detail?: string;
  structuredDetail?: Record<string, unknown>;
  errors?: Record<string, string | string[]>;
  validationErrors?: Record<string, string | string[]>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(record: Record<string, unknown>, key: string): string | undefined {
  const value = record[key];
  return typeof value === 'string' ? value : undefined;
}

function readDetail(record: Record<string, unknown>): string | undefined {
  const detail = record['detail'];
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const parts: string[] = [];
    for (const item of detail) {
      if (!isRecord(item)) continue;
      const msg = item['msg'];
      if (typeof msg !== 'string' || !msg) continue;
      const loc = item['loc'];
      let field: string | undefined;
      if (Array.isArray(loc)) {
        for (let i = loc.length - 1; i >= 0; i--) {
          if (typeof loc[i] === 'string' && loc[i] !== 'body') {
            field = loc[i] as string;
            break;
          }
        }
      }
      parts.push(field ? `${field}: ${msg}` : msg);
    }
    return parts.length > 0 ? parts.join('; ') : undefined;
  }
  return undefined;
}

function readValidationErrors(record: Record<string, unknown>, key: string): Record<string, string | string[]> | undefined {
  const value = record[key];
  if (!isRecord(value)) return undefined;
  const entries = Object.entries(value).filter((entry): entry is [string, string | string[]] => {
    const fieldValue = entry[1];
    return typeof fieldValue === 'string' || (Array.isArray(fieldValue) && fieldValue.every(item => typeof item === 'string'));
  });
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

function parseApiErrorPayload(value: unknown): ApiErrorPayload {
  if (!isRecord(value)) return {};
  const detailRecord = isRecord(value['detail']) ? value['detail'] : undefined;
  return {
    error: readString(value, 'error'),
    reason: readString(value, 'reason'),
    error_description: readString(value, 'error_description'),
    message: readString(value, 'message'),
    detail: readDetail(value),
    structuredDetail: detailRecord,
    errors: readValidationErrors(value, 'errors'),
    validationErrors: readValidationErrors(value, 'validationErrors'),
  };
}

// ============================================================================
// Configuration
// ============================================================================

const BASE_API_URL = config.api.baseUrl;

// ============================================================================
// API Client Instance
// ============================================================================

export const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_API_URL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ============================================================================
// Request Interceptor
// ============================================================================

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Get token from storage
    const token = localStorage.getItem('auth_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  error => Promise.reject(error instanceof Error ? error : new Error('Request interceptor failed.'))
);

// ============================================================================
// Response Interceptor
// ============================================================================

apiClient.interceptors.response.use(
  response => response,
  (error: AxiosError) => {
    // Network error
    if (!error.response) {
      throw new NetworkException('Network error. Please check your connection.');
    }

    const { status } = error.response;
    const data = parseApiErrorPayload(error.response.data);
    const message = data.error_description ?? data.message ?? data.error ?? data.detail ?? 'An error occurred';
    const validationErrors = data.errors ?? data.validationErrors;

    // Handle specific status codes
    switch (status) {
      case 400:
        // Bad Request - Validation errors
        if (validationErrors) {
          throw ValidationException.fromApiErrors(validationErrors, message || 'Validation failed');
        }
        throw new ValidationException({
          message: message || 'Bad request',
          userMessage: message || 'Invalid request. Please check your input.',
        });

      case 401:
        // Clear token and redirect to login
        localStorage.removeItem('auth_token');
        window.location.href = '/auth';
        throw new AuthenticationException('Session expired. Please log in again.');

      case 403:
        throw new AuthorizationException({
          message: message || 'Forbidden',
          userMessage: 'You do not have permission to perform this action.',
          statusCode: 403,
        });

      case 404:
        throw new NotFoundException({
          message: message || 'Resource not found',
          userMessage: 'The requested resource could not be found.',
        });

      case 409:
        if (data.error === 'client_revoked') {
          throw new AppException({
            code: ErrorCode.RESOURCE_CONFLICT,
            message: message || 'Resource conflict',
            userMessage: 'This OAuth client has been revoked and can no longer be changed.',
            statusCode: 409,
            metadata: { apiErrorCode: data.error },
          });
        }
        if (data.structuredDetail) {
          const detailObj = data.structuredDetail;
          const resolved =
            data.error_description ??
            data.message ??
            data.error ??
            readString(detailObj, 'error_description') ??
            readString(detailObj, 'message') ??
            readString(detailObj, 'error') ??
            'Resource conflict';
          const code = readString(detailObj, 'error') ?? data.error;
          throw new AppException({
            code: ErrorCode.RESOURCE_CONFLICT,
            statusCode: 409,
            message: resolved,
            userMessage: resolved,
            metadata: {
              apiErrorCode: code,
              apiErrorDetails: detailObj,
            },
          });
        }
        if (data.reason?.trim() && !data.error && !data.error_description && !data.message && !data.detail) {
          throw new AppException({
            code: ErrorCode.RESOURCE_CONFLICT,
            message: data.reason,
            userMessage: 'This resource conflicts with an existing state.',
            statusCode: 409,
            metadata: { apiErrorCode: data.reason },
          });
        }
        throw new ValidationException({
          message: message || 'Resource conflict',
          userMessage: 'This resource already exists or conflicts with existing data.',
        });

      case 422:
        // Unprocessable Entity - Semantic validation errors
        if (validationErrors) {
          throw ValidationException.fromApiErrors(validationErrors, message || 'Validation failed');
        }
        throw new ValidationException({
          message: message || 'Unprocessable entity',
          userMessage: message || 'Unable to process the request. Please check your input.',
        });

      case 500:
      case 502:
      case 503:
      case 504:
        throw new ServerException({
          message,
          userMessage: 'Something went wrong on our end. Please try again later.',
          statusCode: status,
        });

      default:
        throw error;
    }
  }
);

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Type-safe GET request
 */
export async function get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const response = await apiClient.get<T>(url, { params });
  return response.data;
}

/**
 * Type-safe POST request
 */
export async function post<T>(url: string, data?: unknown): Promise<T> {
  const response = await apiClient.post<T>(url, data);
  return response.data;
}

/**
 * Type-safe PUT request
 */
export async function put<T>(url: string, data?: unknown): Promise<T> {
  const response = await apiClient.put<T>(url, data);
  return response.data;
}

/**
 * Type-safe PATCH request
 */
export async function patch<T>(url: string, data?: unknown): Promise<T> {
  const response = await apiClient.patch<T>(url, data);
  return response.data;
}

/**
 * Type-safe DELETE request
 */
export async function del<T>(url: string, data?: unknown): Promise<T> {
  const response = data !== undefined ? await apiClient.delete<T>(url, { data }) : await apiClient.delete<T>(url);
  return response.data;
}
